import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const cardWrapperSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
};

export const cardSx = (expanded: boolean): SxProps<Theme> => ({
    position: 'relative',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    backgroundColor: expanded ? '#EEF1F5' : '#F5F7FA',
    borderRadius: expanded ? '10px 10px 0 0' : '10px',
    py: 1,
    px: 2,
    minHeight: '56px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    color: theme.palette.text.primary,
    cursor: 'pointer',
    overflow: 'hidden',
    transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
    '&:hover': {
        backgroundColor: '#EEF1F5',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    },
    [theme.breakpoints.down('md')]: {
        px: 1.5,
    },
});

export const directionIconBoxSx = (dir: 'in' | 'out'): SxProps<Theme> => ({
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    backgroundColor: dir === 'out'
        ? 'rgba(76, 175, 80, 0.14)'
        : 'rgba(25, 118, 210, 0.14)',
    color: dir === 'out'
        ? theme.palette.success.main
        : theme.palette.primary.main,
    '& svg': {
        fontSize: '16px',
    },
});

export const timeColSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 1,
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        gap: 0.5,
    },
};

export const timeSx: SxProps<Theme> = {
    fontSize: '14px',
    fontFamily: 'monospace',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
};

export const dateSx: SxProps<Theme> = {
    fontSize: '12px',
    color: theme.palette.neutral[500],
    lineHeight: 1.2,
    fontFamily: 'monospace',
};

export const dividerSx: SxProps<Theme> = {
    width: '1px',
    height: '28px',
    backgroundColor: theme.palette.neutral[200],
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        display: 'none',
    },
};

export const innerDividerSx: SxProps<Theme> = {
    width: '1px',
    height: '28px',
    backgroundColor: theme.palette.neutral[200],
    flexShrink: 0,
};

export const typeColSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
        gap: 1,
    },
};

export const actionGroupSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
    overflow: 'hidden',
};

export const logIdSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 0.625,
    color: theme.palette.neutral[500],
    whiteSpace: 'nowrap',
    flexShrink: 0,
};

export const logIdLabelSx: SxProps<Theme> = {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: '11px',
    color: theme.palette.neutral[500],
};

export const logIdValueSx: SxProps<Theme> = {
    fontFamily: 'monospace',
    fontSize: '12px',
    fontWeight: 600,
    color: theme.palette.neutral[800],
};

export const dotSx = (color: string): SxProps<Theme> => ({
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: color,
    flexShrink: 0,
});

export const actionTextSx: SxProps<Theme> = {
    fontSize: '14px',
    fontWeight: 600,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
};

export const expandIconSx = (expanded: boolean): SxProps<Theme> => ({
    color: theme.palette.neutral[500],
    transition: 'transform 160ms ease',
    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
    flexShrink: 0,
    fontSize: '20px',
});

export const expandedPanelSx: SxProps<Theme> = {
    backgroundColor: '#FAFBFD',
    borderRadius: '0 0 10px 10px',
    border: '1px solid rgba(0,0,0,0.05)',
    borderTop: 'none',
    px: 2,
    py: 1.5,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
};

export const metaStripSx: SxProps<Theme> = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 0,
    marginBottom: 1.25,
    rowGap: 0.5,
};

export const metaItemSx: SxProps<Theme> = {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 0.75,
    px: 1.25,
    borderRight: `1px solid ${theme.palette.neutral[200]}`,
    '&:last-of-type': {
        borderRight: 'none',
    },
    '&:first-of-type': {
        paddingLeft: 0,
    },
    [theme.breakpoints.down('sm')]: {
        borderRight: 'none',
        paddingLeft: 0,
        paddingRight: 0,
        flexBasis: '100%',
    },
};

export const metaLabelSx: SxProps<Theme> = {
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.neutral[500],
    fontSize: '10px',
};

export const metaValueSx: SxProps<Theme> = {
    fontFamily: 'monospace',
    color: theme.palette.neutral[800],
    fontSize: '12px',
    wordBreak: 'break-all',
};

export const codeBlockSx: SxProps<Theme> = {
    backgroundColor: theme.palette.text.secondary,
    border: '1px solid rgba(0,0,0,0.06)',
    borderRadius: '8px',
    padding: 1.5,
    fontFamily: 'monospace',
    fontSize: '12px',
    color: theme.palette.neutral[800],
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
    overflow: 'auto',
    maxHeight: '400px',
    lineHeight: 1.5,
};
